import { defineStore } from 'pinia';
import {
  getSpecification,
  getFpcCodeTranslations,
  getConfigurationV2,
  getMarketTranslations,
} from '../services/digitalSalesService';
import imagePlaceholder from '../assets/placeholder.png';
import { useConfiguratorApi } from '../composables/configurator';
import { v4 as uuidv4 } from 'uuid';

const { getImagePackage } = useConfiguratorApi();
const getDefaultImages = () => ({
  view: imagePlaceholder,
  front: imagePlaceholder,
  side: imagePlaceholder,
  interior: imagePlaceholder,
  dashboard: imagePlaceholder,
});

export const useMainStore = defineStore('main', {
  state: () => ({
    webpSupported: false,
    activePage: null,
    offerId: '',
    offerStatus: {},
    offerLoadDataError: false,
    fpcCodeTranslations: [],
    options: null,
    basePrice: null,
    repairMaintenancePrice: null,
    images: getDefaultImages(),
    imageRequestVersion: 0,
    selected: {},
    specification: null,
    token: null,
    locale: null,
    fallbackLocale: 'en-GB',
    translations: {},
    languages: [
      { locale: 'en-GB', name: 'English', description: 'English' },
      { locale: 'pl-PL', name: 'Polish', description: 'Polish' },
      { locale: 'de-DE', name: 'German', description: 'German (Germany)' },
      { locale: 'de-CH', name: 'German', description: 'German (Switzerland)' },
      { locale: 'fr-CH', name: 'French', description: 'French (Switzerland)' },
      { locale: 'it-CH', name: 'Italian', description: 'Italian (Switzerland)' },
      { locale: 'ja-JP', name: 'Japanese', description: 'Japanese' },
    ],
    sections: [
      { id: 'powertrain', name: 'drivelineHeaderTitle' },
      { id: 'cab-color', name: 'cabColourHeaderTitle' },
      { id: 'styling-comfort', name: 'stylingComfortHeaderTitle' },
      { id: 'chassis', name: 'chassisHeaderTitle' },
      { id: 'fifth-wheel', name: 'fifthWheelHeaderTitle' },
      { id: 'safety', name: 'safetyHeaderTitle' },
      { id: 'fuel-efficiency', name: 'fuelEfficiencyHeaderTitle' },
      { id: 'connectedServices', name: 'connectedServicesHeaderTitle' },
      { id: 'repairMaintenance', name: 'repairMaintenanceHeaderTitle' },
    ],
    appLoading: true,
    supportRequested: false,
    isAdmin: false,
    isSuperUser: false,
    isSalesman: false,
    externalStaffReference: null,
    federationUserAccountId: null,
    misc: {},
    selectedPaymentType: 'leasing',
    selectedPaymentCurrency: 'EUR',
    currencies: [],
    selectedNumberVehicles: 1,
    tid: null,
    sectionMetadata: {},
    creationReferenceData: {},
    toasts: [],
    market: null,
    markets: [
      {
        marketId: 'pl',
        description: 'Poland',
        defaultLocale:'pl-PL',
        defaultHeroImageType: 'view',
        locales: ['pl-PL', 'en-GB'],
        showLanguageSelection: false,
        pageTitle: '',
        linksMetadata: [
          { title: 'privacyPolicyText', link: 'https://www.scania.com/pl/pl/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/pl/pl/home/admin/misc/privacy-statement/cookies.html', footer: false },
          { title: 'environmentalPolicyText', link: 'https://www.scania.com/pl/pl/home/admin/misc/scania-environmental-policy.html' },
          { title: 'gdprText', link: 'https://www.scania.com/pl/pl/home/admin/misc/rodo.html' },
        ],
        highlightColor: 'var(--sdds-red-500)',
        showDealerSelection: true,
        availableDealers: [
          {
              branchName: "Nie znam swojego dealera",
              streetAddress: "",
              postalCode: "",
              city: "",
          },
          {
              branchName: "Białystok",
              streetAddress: "ul. Warszawska 76",
              postalCode: "16-070",
              city: "Choroszcz",
          },
          {
              branchName: "Bielsko-Biała",
              streetAddress: "ul. Międzyrzecka 250",
              postalCode: "43-382",
              city: "Bielsko Biała",
          },
          {
              branchName: "Bolesławiec",
              streetAddress: "ul. Szwedzka 1C",
              postalCode: "59-225",
              city: "Okmiany",
          },
          {
              branchName: "Częstochowa",
              streetAddress: "ul. Częstochowska 37",
              postalCode: "42-233",
              city: "Mykanów",
          },
          {
              branchName: "Gdańsk",
              streetAddress: "ul. Energetyczna 17, Kowale gm. Kolbudy",
              postalCode: "80-180",
              city: "Gdańsk",
          },
          {
              branchName: "Gliwice",
              streetAddress: "ul. Rybnicka 197",
              postalCode: "44-122",
              city: "Gliwice",
          },
          {
              branchName: "Grajewo",
              streetAddress: "ul. Wiórowa 11",
              postalCode: "19-203",
              city: "Grajewo",
          },
          {
              branchName: "Kalisz",
              streetAddress: "ul. Kaliska 10, Fabianów",
              postalCode: "63-460",
              city: "Nowe Skalmierzyce",
          },
          {
              branchName: "Kielce",
              streetAddress: "ul. Barcza 5c",
              postalCode: "26-050",
              city: "Zagnańsk",
          },
          {
              branchName: "Koszalin",
              streetAddress: "ul. Piwonii 39",
              postalCode: "76-039",
              city: "Nowe Bielice",
          },
          {
              branchName: "Kraków",
              streetAddress: "ul. Tyniecka 231",
              postalCode: "30-376",
              city: "Kraków",
          },
          {
              branchName: "Kwidzyn",
              streetAddress: "ul. Lotnicza 1",
              postalCode: "82-500",
              city: "Kwidzyn",
          },
          {
              branchName: "Leszno",
              streetAddress: "ul. Klonowiec 4a",
              postalCode: "64-111",
              city: "Lipno",
          },
          {
              branchName: "Lublin",
              streetAddress: "ul. Piasecka 20",
              postalCode: "21-040",
              city: "Świdnik",
          },
          {
              branchName: "Mława",
              streetAddress: "Płocka 128",
              postalCode: "06-500",
              city: "Mława",
          },
          {
              branchName: "Nadarzyn",
              streetAddress: "Aleja Katowicka 316, Stara Wieś",
              postalCode: "05-830",
              city: "Nadarzyn",
          },
          {
              branchName: "Olsztyn",
              streetAddress: "ul. Lubelska 46",
              postalCode: "10-406",
              city: "Olsztyn",
          },
          {
              branchName: "Opole",
              streetAddress: "ul. Wspólna 6",
              postalCode: "45-837",
              city: "Opole",
          },
          {
              branchName: "Ostrołęka",
              streetAddress: "Laski Szlacheckie 60",
              postalCode: "07-407",
              city: "Laski Szlacheckie",
          },
          {
              branchName: "Piła",
              streetAddress: "ul. Franciszka Garczyńskiego 10",
              postalCode: "64-920",
              city: "Piła",
          },
          {
              branchName: "Poznań",
              streetAddress: "ul. Poznańska 171",
              postalCode: "62-080",
              city: "Tarnowo Podgórne",
          },
          {
              branchName: "Radzymin",
              streetAddress: "Al. Jana Pawła II 24, Słupno",
              postalCode: "05-250",
              city: "Radzymin",
          },
          {
              branchName: "Rzeszów",
              streetAddress: "ul. Ludwika Chmury 18",
              postalCode: "35-213",
              city: "Rzeszów",
          },
          {
              branchName: "Sandomierz",
              streetAddress: "ul. Trześniowska 7A",
              postalCode: "27-600",
              city: "Sandomierz",
          },
          {
              branchName: "Siedlce",
              streetAddress: "ul. Warszawska 45, Gręzów",
              postalCode: "08-130",
              city: "Kotuń",
          },
          {
              branchName: "Szczecin",
              streetAddress: "ul. Struga 80",
              postalCode: "70-777",
              city: "Szczecin",
          },
          {
              branchName: "Słubice",
              streetAddress: "Osiedle Przemysłowe 3C",
              postalCode: "69-100",
              city: "Słubice",
          },
          {
              branchName: "Słupsk",
              streetAddress: "ul. Inwestycyjna 9",
              postalCode: "76-200",
              city: "Słupsk",
          },
          {
              branchName: "Wrocław",
              streetAddress: "ul Fabryczna 21, Pietrzykowice",
              postalCode: "55-080",
              city: "Kąty Wrocławskie",
          },
          {
              branchName: "Zielona Góra",
              streetAddress: "ul. Zielonogórska 138",
              postalCode: "67-100",
              city: "Nowa Sól",
          },
        ],
        showPrice: true,
        showPriceTooltip: true,
        showRMSliders: true,
        showFiltersOnActiveOffersPage: true,
        showHeroSectionFinancingText: true,
        showStickySupportButton: true,
        showConfigureChevron: true,
      },
      {
        marketId: 'de',
        description: 'Germany',
        defaultLocale:'de-DE',
        defaultHeroImageType: 'view',
        locales: ['de-DE', 'en-GB'],
        showLanguageSelection: false,
        pageTitle: '',
        linksMetadata: [
          { title: 'privacyPolicyText', link: 'https://www.scania.com/de/de/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/de/de/home/admin/misc/privacy-statement/cookies.html' },
        ],
        highlightColor: 'var(--sdds-blue-400)',
        showDealerSelection: false,
        availableDealers: [],
        showPrice: true,
        showPriceTooltip: true,
        showRMSliders: true,
        showFiltersOnActiveOffersPage: true,
        showHeroSectionFinancingText: true,
        showStickySupportButton: true,
        showConfigureChevron: true,
      },
      {
        marketId: 'ch',
        description: 'Switzerland',
        defaultLocale:'de-CH',
        defaultHeroImageType: 'view',
        locales: ['de-CH', 'fr-CH', 'it-CH'],
        showLanguageSelection: true,
        pageTitle: '',
        linksMetadata: [
          { title: 'privacyPolicyText', link: 'https://www.scania.com/ch/de/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/ch/de/home/admin/misc/privacy-statement/cookies.html', footer: false },
          { title: 'environmentalPolicyText', link: 'https://www.scania.com/ch/de/home/about-scania/sustainability/environmental-footprint.html' },
          // { title: 'gdprText', link: 'https://www.scania.com/pl/pl/home/admin/misc/rodo.html' },
        ],
        highlightColor: 'var(--sdds-blue-400)',
        showDealerSelection: false,
        availableDealers: [],
        showPrice: true,
        showPriceTooltip: false,
        showRMSliders: true,
        showFiltersOnActiveOffersPage: true,
        showHeroSectionFinancingText: true,
        showStickySupportButton: true,
        showConfigureChevron: true,
      },
      {
        marketId: 'jp',
        description: 'Japan',
        defaultLocale:'ja-JP',
        defaultHeroImageType: 'view',
        locales: ['ja-JP', 'en-GB'],
        showLanguageSelection: true,
        pageTitle: 'Prime Ready',
        linksMetadata: [
          { title: 'privacyPolicyText', link: 'https://www.scania.com/jp/ja/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/jp/ja/home/admin/misc/privacy-statement/cookies.html', footer: false },
          // { title: 'sustainabilityText', link: 'https://www.scania.com/jp/ja/home/about-scania/sustainability.html' },
        ],
        highlightColor: 'var(--sdds-blue-400)',
        showDealerSelection: false,
        availableDealers: [],
        showPrice: false,
        showPriceTooltip: false,
        showRMSliders: false,
        showFiltersOnActiveOffersPage: false,
        showHeroSectionFinancingText: false,
        showStickySupportButton: false,
        showConfigureChevron: false,
      },
    ],
    showYearEndOfferBanner: false,
    frameAgreementAvailable: null,
    featureToggleData: {},
    vehicleCardImage: {},
    serviceSavings: [],
  }),
  getters: {
    getSelected(state) {
      return state.selected;
    },
    getModifiedSpecification(state) {
      const specImpacts = this.getSpecImpactGrouped(state.selected).map(x => x.specImpact).flat(1);
      return state.specification?.map(item => ({
        ...item,
        selections: item.selections.map(subItem => {
          const impact = subItem.id === 'cabColour'
            ? specImpacts.find(x => x.id === subItem.id && !x.value.endsWith('-'))
            : specImpacts.find(x => x.id === subItem.id);
          return impact ? { id: impact.id, fpcCode: impact.value } : subItem;
        }),
      }));
    },
    getOfferStatus(state) {
      return state.offerStatus;
    },
    getLocale () {
      return this.locale || this.getDefaultLocale;
    },
    getDefaultLocale() {
      const market = this.markets.find(m => m.marketId === this.market);
      return market?.defaultLocale || this.fallbackLocale;
    },
    getLanguages() {
      const market = this.markets.find(m=>m.marketId === this.market);
      const locales = market?.locales || [this.fallbackLocale];
      return this.languages.filter(l => locales.includes(l.locale));
    },
    footerLinkMetadataForActiveMarket(state) {
      // Return all link metadata for active market that do not have "footer" set to false
      return state.markets.find(market => market.marketId === state.market)?.linksMetadata?.filter(item => item.footer !== false) ?? [];
    },
    activeMarket(state) {
      // Return the active market configuration
      return state.markets.find(market => market.marketId === state.market) ?? null;
    },
    shouldShowYear2024OfferBanner() {
      // Do not show banner if a frame agreement is being configured
      if (this.misc?.frameAgreement) {
        return false;
      }

      // Do not show banner if a frame agreement is available and the home / active offers page is open
      if (this.frameAgreementAvailable !== false && (this.activePage ===null || this.activePage === 'HOME' || this.activePage === 'ACTIVE_OFFERS')) {
        return false;
      }

      // Parse the feature toggle data
      const parsedFeatureToggle = this.featureToggleData[`${this.market}#webapp_year2024OfferBanner`];

      // Return the isActive flag if parse succeeded, otherwise false
      return parsedFeatureToggle ? parsedFeatureToggle.isActive && parsedFeatureToggle.isFeatureDateValid : false;
    },
  },
  actions: {
    resetOfferData() {
      this.offerId = '';
      this.offerStatus = {};
      this.options = null;
      this.basePrice = null;
      this.repairMaintenancePrice = null;
      this.images = {
        view: imagePlaceholder,
        front: imagePlaceholder,
        side: imagePlaceholder,
        interior: imagePlaceholder,
        dashboard: imagePlaceholder,
      },
      this.selected = {};
      this.specification = null;
      this.misc = {};
      this.selectedPaymentType = 'leasing';
      this.selectedPaymentCurrency = 'EUR';
      this.currencies = [];
      this.sectionMetadata = {};
      this.creationReferenceData = {};
    },
    setOfferId(id) {
      this.offerId = id;
    },
    getTranslation(key, locale = this.locale) {
      const marketKey =  this.market + String(key)?.charAt(0)?.toUpperCase() + String(key)?.slice(1);
      return this.translations[locale]?.[key] ||
        this.translations[locale]?.[marketKey] ||
        this.translations[this.fallbackLocale]?.[key] ||
        this.translations[this.fallbackLocale]?.[marketKey] ||
        key;
    },
    getSpecImpactGrouped(selections) {
      return Object.entries(selections)
      .map(([groupKey, group]) => {
        if (['leasing', 'payment', 'driverServices', 'connectedServices', 'highlights'].includes(groupKey)) return [];
        return Object.entries(group).map(([key, selection]) => ({ key, specImpact: selection.specImpact }));
      })
      .flat(1);
    },
    updateSelected(property, value) {
      this.selected[property] = value;
    },
    async loadTranslations() {
      if (this.translations[this.getLocale]) return;
      this.translations[this.getLocale] = await (await getMarketTranslations(this.market, this.getLocale)).data;
    },
    loadSpecification() {
      getSpecification()
      .then(response => {
        this.specification = response.data;
        this.loadSpecificationTranslations();
      });
    },
    loadSpecificationTranslations() {
      const fpcCodes = this.specification?.reduce((finalList, category) => {
        const categoryList = category.selections.reduce((categoryList, item) => [...categoryList, item.fpcCode], []);
        return [...finalList, ...categoryList];
      }, []).filter(Boolean);
      getFpcCodeTranslations(fpcCodes, this.locale)
      .then(response => {
        this.fpcCodeTranslations.push(...response.data);
      }).catch((e) => {
        console.error(e);
      });
    },
    async loadConfiguration() {
      this.selected = {};
      this.offerLoadDataError = false;
      this.imageRequestVersion += 1;
      this.images = getDefaultImages();
      let response = null;
      try {
        response = await getConfigurationV2(); 
      } catch (error) {
        this.offerLoadDataError = true;
        return;
      }
      this.offerStatus = response.data.statuses;
      const { regulationsApprovedForUser, ...rest } = this.offerStatus;
      if (Object.values(rest).some(Boolean) || !regulationsApprovedForUser) {
        return;
      }
      const availablePaymentType = response.data.options.payment?.find(o => o.id === 'paymentType')?.options?.find(o => o.default)
      ?? response.data.options.payment?.find(o => o.id === 'paymentType')?.options[0];
      this.options = response.data.options;
      this.basePrice = response.data.basePrice;
      this.repairMaintenancePrice = response.data.repairMaintenancePrice;
      this.misc = response.data.misc;
      this.selectedPaymentType = availablePaymentType?.value ?? 'leasing';
      this.selectedPaymentCurrency = response.data.options.payment?.find(o => o.id === 'paymentCurrency')?.options?.find(o => o.default)?.id ?? 'EUR';
      this.currencies = response.data.options.payment?.find(o => o.id === 'paymentCurrency')?.options ?? [];
      this.setConfigurationDefaults();
      this.loadConfigurationTranslations();
      this.sectionMetadata = response.data.sectionMetadata;
      this.creationReferenceData = response.data.creationReferenceData || {};
      this.serviceSavings = response.data.serviceSavings || [];
      return;
    },
    loadConfigurationTranslations() {
      const optionKeys = Object.keys(this.options || {});
      const fpcCodes = [];
      optionKeys.forEach(key => {
        this.options[key].forEach(item => {
          item.options.forEach(option => {
            const tempCodes = option.specImpact.flatMap(x =>
              (x.value && !x.value.endsWith('-'))
              ? [x.value]
              : [],
            );
            fpcCodes.push(...tempCodes);
          });
        });
      });
      const uniqueCodes = [...new Set(fpcCodes)];
      getFpcCodeTranslations(uniqueCodes, this.locale)
      .then(response => {
        this.fpcCodeTranslations.push(...response.data);
      }).catch((e) => {
        console.error(e);
      });
    },
    setConfigurationDefaults() {
      const optionKeys = Object.keys(this.options);
      optionKeys.forEach(key => {
        this.options[key].forEach(item => {
          const defaultVal = item.options.find(x => x.default);
          if (!this.selected[key]) this.selected[key] = {};
          this.selected[key][item.id] = defaultVal || null;
        });
      });
      this.updateImages(this.selected);
    },
    updateImages(options) {
      const requestVersion = ++this.imageRequestVersion;
      getImagePackage(this.getSpecImpactGrouped(options))
      .then(response => {
        // Ignore outdated responses so older requests cannot overwrite newer selections.
        if (requestVersion !== this.imageRequestVersion) return;
        this.images = { ...getDefaultImages(), ...response };
      })
      .catch((error) => {
        if (requestVersion !== this.imageRequestVersion) return;
        console.error('updateImages-error', error);
      });
    },
    addToast(toast) {
      toast.id = uuidv4();
      this.toasts.push(toast);
    },
    addSuccessToast(toast, options) {
      if (typeof toast === 'string') {
        toast = {
          header: toast,
        };
      }
      this.addToast({
        ...toast,
        translate: options?.translate,
        type: 'success',
      });
    },
    addErrorToast(toast, options) {
      if (typeof toast === 'string') {
        toast = {
          header: toast,
        };
      }
      this.addToast({
        ...toast,
        translate: options?.translate,
        type: 'error',
      });
    },
    clearToast(id) {
      this.toasts = this.toasts.filter(x => x.id !== id);
    },
  },
});
